import { useMemo } from 'react';

import useStudentHomeDetails from '@~/hooks/use-student-home-details';

export enum EMathgymSubscriptionType {
  INFINITE = 'infinite',
  BASIC = 'basic',
}

interface IUseIsMathgymPremium {
  (): {
    isPremiumUser: boolean;
    isPremiumExpired: boolean;
    hasActiveSubscription: boolean;
    isSubscriptionPlanNotSynched: boolean;
  };
}

/**
 * Checks if the user has an active premium subscription.
 * Used for showing premium UI elements (infinity image in header, etc.)
 * Also returns premium expiry date and whether premium has expired.
 */
const useIsMathgymPremium: IUseIsMathgymPremium = () => {
  const { student } = useStudentHomeDetails();
  const entitlements = student?.entitlements;

  const premiumEntitlement = useMemo(() => {
    if (!entitlements?.length) return null;

    // return the entitlement with the latest expiry date, in case there are multiple entitlements
    return entitlements.slice().sort((a, b) => b.expires_at_ms - a.expires_at_ms)[0];
  }, [entitlements]);

  const isPremiumUser = useMemo(() => {
    return student?.math_gym_enrolled_user ?? false;
  }, [student?.math_gym_enrolled_user]);

  const isPremiumExpired = useMemo(() => {
    if (!premiumEntitlement || student?.math_gym_enrolled_user) return false;

    return !premiumEntitlement.is_active;
  }, [premiumEntitlement, student?.math_gym_enrolled_user]);

  const hasActiveSubscription = useMemo(() => {
    return premiumEntitlement?.is_active ?? false;
  }, [premiumEntitlement]);

  const isSubscriptionPlanNotSynched = useMemo(() => {
    if (!premiumEntitlement) return false;

    // for individual plan there should be student ids enabled in the entitlement.
    // If there are no enabled student ids in the entitlement,
    // it means that the individual plan subscription details are not synched properly with the student details.
    if (
      !premiumEntitlement.is_family_plan &&
      premiumEntitlement.enabled_student_ids.length === 0
    ) {
      return true;
    }

    return false;
  }, [premiumEntitlement]);

  // Memoize return value to maintain stable object reference
  return useMemo(
    () => ({
      isPremiumUser,
      isPremiumExpired,
      hasActiveSubscription,
      isSubscriptionPlanNotSynched,
    }),
    [
      isPremiumUser,
      isPremiumExpired,
      hasActiveSubscription,
      isSubscriptionPlanNotSynched,
    ],
  );
};

export default useIsMathgymPremium;
